import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  As400ExportGroupSchema,
  As400UploadModeOverrideSchema,
  As400UploadModeSchema,
  ExportFileStreamSchema,
  type As400ExportGroup,
} from "./products.contract";

/**
 * Publishing a review's AS/400 upload file to Rexel — writing it into the blob
 * folder their AS/400 job picks files up from, instead of downloading it for
 * someone to upload by hand.
 *
 * The file itself is the same one the download produces (see
 * `prepareAs400Export`), written in the shape that job reads: tab-separated,
 * **no header row**, values verbatim.
 *
 * Everything about the destination is dictated by their process, so it lives
 * here as named constants rather than as string literals at the write site:
 * one file per worksheet per run, named for the mode, the worksheet and the
 * branch it loads into.
 *
 * **The unit of publish is a branch, not a file.** AS/400 loads a branch at a
 * time and takes every file it finds in the folder on that run, so a branch's
 * whole set — one file for each worksheet the review has edits in — goes up
 * together. Sending one worksheet and holding the rest back would load a partial
 * picture of the same records.
 *
 * **The rules in full, and why each one is a rule, are in
 * `docs/inventory-management/as400-publishing.md`.** Read it before changing a
 * name, a delimiter or the folder gate — every one of those is load-bearing on
 * their side, and getting one wrong writes bad records into a live catalog
 * without erroring.
 */

/**
 * Which population a published file carries — the same split the export makes
 * (`As400ExportMode`), because it is the same AS/400 rule: a run that adds
 * records and a run that changes existing ones are different jobs, so adds and
 * changes can never share a file.
 */
export const As400PublishModeSchema = As400UploadModeSchema;
export type As400PublishMode = z.infer<typeof As400PublishModeSchema>;

/**
 * The filename's leading segment, which is what tells AS/400 which run this
 * file is for: `I1` adds records, `I2` changes them. Loading a file in the wrong
 * one is destructive, which is why the mode is never implicit.
 */
export const AS400_PUBLISH_MODE_SEGMENT: Record<As400PublishMode, string> = {
  ADD: "I1",
  CHANGE: "I2",
};

/**
 * Second segment: which worksheet's file this is. Rexel's names, not ours — the
 * loader routes on them, so they are not derived from our own group keys.
 *
 * Only the worksheets whose segment Rexel has actually given us are listed —
 * publishing is offered for those alone. A worksheet with no entry can still be
 * exported and uploaded by hand; naming its file from a guess would put a file
 * their job can't route into the folder it polls.
 */
export const AS400_PUBLISH_WORKSHEET_SEGMENT: Partial<
  Record<As400ExportGroup, string>
> = {
  ITEM: "ITEM",
  PURCHASE_DATA: "PURMAS",
  PRICE: "PRICE",
  ITEM_ADD: "ITEMAD",
  // STOCK and STKAD confirmed by a 2026-10-23 test upload: the AS/400 response
  // reported the rows as `RD.STOCK` and `RD.STKAD`. A file named `STCKAD` was
  // not picked up at all ("Stock Add: 0 records read").
  STOCK: "STOCK",
  STOCK_ADD: "STKAD",
  ITEM_KW: "ITEMKW",
  MATRIX_CLASSIFICATION: "MXCLS",
};

/** The worksheet's filename segment, or null when publishing it isn't set up. */
export function as400PublishWorksheetSegment(
  group: As400ExportGroup,
): string | null {
  return AS400_PUBLISH_WORKSHEET_SEGMENT[group] ?? null;
}

/**
 * The worksheets whose filename carries the branch — only the item master.
 *
 * Every other worksheet's file is named without it, so `I1_PRICE_202609151530`
 * rather than `I1_PRICE_A_202609151530`. This is Rexel's rule and it is the
 * reason the name is built here rather than assembled at the write site: the
 * loader reads the name positionally, so a branch segment on a file that should
 * not carry one lands in the slot it reads the timestamp from.
 *
 * A batch is still one branch's drop. The item file in it names the branch; the
 * rest ride along in the same run.
 */
export const AS400_PUBLISH_BRANCH_NAMED_WORKSHEETS: ReadonlySet<As400ExportGroup> =
  new Set<As400ExportGroup>(["ITEM"]);

/** Whether this worksheet's filename carries the branch. */
export function as400PublishNamesBranch(group: As400ExportGroup): boolean {
  return AS400_PUBLISH_BRANCH_NAMED_WORKSHEETS.has(group);
}

/**
 * The name Rexel's job expects: mode, worksheet, the branch **only for the item
 * master**, and the time the file was written as `YYYYMMDDHHMM` (Eastern —
 * `timestamp` arrives already formatted, so this stays a pure join). No
 * extension: the job matches on the name as given.
 *
 * ```
 * I1_ITEM_A_202609151530   the item file, which names its branch
 * I1_PRICE_202609151530    every other worksheet, which does not
 * ```
 *
 * There is no source segment. The name used to carry `GNT` between the mode and
 * the worksheet, the way the S&D VDM files do (`I1_GNTVDM_…`); Rexel dropped it
 * for these files on 15 Sep 2026.
 *
 * Their loader parses the name positionally, which is why both of those are
 * load-bearing rather than cosmetic: an extra segment — a stale `GNT`, or a
 * branch on a file that should not carry one — shifts everything after it into
 * the slot the loader reads something else from.
 */
export function as400PublishFileName(params: {
  mode: As400PublishMode;
  /** From {@link as400PublishWorksheetSegment}. */
  worksheet: string;
  /**
   * The AS/400 branch (location) the file loads into — a single code, and only
   * for a worksheet that names it ({@link as400PublishNamesBranch}). Null leaves
   * the segment out entirely.
   */
  branch: string | null;
  /** `YYYYMMDDHHMM`. */
  timestamp: string;
}): string {
  return [
    AS400_PUBLISH_MODE_SEGMENT[params.mode],
    params.worksheet,
    ...(params.branch === null ? [] : [params.branch]),
    params.timestamp,
  ].join("_");
}

/**
 * A published filename read back: which run the file is for, which worksheet it
 * carries, and — for the item file alone — which branch it loads into.
 *
 * The inverse of {@link as400PublishFileName}, kept beside it so the two stay in
 * step. The name is the only thing that says what a file is, and their loader
 * reads it positionally, so this reads it positionally too: every segment is
 * checked rather than pattern-matched loosely, and a name that doesn't parse is
 * reported rather than guessed at (`docs/inventory-management/as400-publishing.md`
 * §2, §10.3).
 */
export interface ParsedAs400PublishFileName {
  mode: As400PublishMode;
  group: As400ExportGroup;
  /**
   * The branch segment, for the one worksheet that carries it
   * ({@link as400PublishNamesBranch}) — the item file. Null for every other
   * worksheet, whose name has no branch segment at all.
   */
  branch: string | null;
  /** The name's `YYYYMMDDHHMM` stamp. */
  timestamp: string;
}

/** Why a filename could not be read as one of these files. */
export const As400PublishFileNameProblemSchema = z.enum([
  /** Not `MODE_WORKSHEET[_BRANCH]_STAMP` at all. */
  "SHAPE",
  /** Leading segment is neither `I1` nor `I2`. */
  "MODE",
  /** Second segment names no worksheet in {@link AS400_PUBLISH_WORKSHEET_SEGMENT}. */
  "WORKSHEET",
  /** A branch segment on a worksheet that never names one, or missing from the item file. */
  "BRANCH",
  /** Final segment is not a `YYYYMMDDHHMM` stamp. */
  "TIMESTAMP",
]);
export type As400PublishFileNameProblem = z.infer<
  typeof As400PublishFileNameProblemSchema
>;

/** Worksheet filename segment → the worksheet, reversed from the forward map. */
const AS400_PUBLISH_GROUP_BY_SEGMENT: ReadonlyMap<string, As400ExportGroup> =
  new Map(
    Object.entries(AS400_PUBLISH_WORKSHEET_SEGMENT).flatMap(
      ([group, segment]) =>
        segment === undefined
          ? []
          : [[segment, group as As400ExportGroup] as const],
    ),
  );

/** The worksheet a filename segment names, or null when it names none. */
export function as400PublishWorksheetFromSegment(
  segment: string,
): As400ExportGroup | null {
  return AS400_PUBLISH_GROUP_BY_SEGMENT.get(segment.toUpperCase()) ?? null;
}

/** The run a filename's leading segment names, or null when it names none. */
export function as400PublishModeFromSegment(
  segment: string,
): As400PublishMode | null {
  const wanted = segment.toUpperCase();
  for (const mode of As400PublishModeSchema.options) {
    if (AS400_PUBLISH_MODE_SEGMENT[mode] === wanted) return mode;
  }
  return null;
}

/**
 * The item file's branch segment for a drop applied at several locations: the
 * item file carries `@` in its branch slot, and a location-list file (`ITMSL`)
 * riding beside it lists the locations, one per line. Rexel's loader applies
 * every other file in the drop once per location listed.
 */
export const AS400_PUBLISH_ALL_LOCATIONS_BRANCH = "@";

/**
 * A single alphanumeric branch code, as an AS/400 location key is — or `@`,
 * the item file of a drop that a location list fans out
 * ({@link AS400_PUBLISH_ALL_LOCATIONS_BRANCH}).
 */
const PUBLISH_BRANCH_SEGMENT_RE = /^[0-9A-Z@]$/;

/** `YYYYMMDDHHMM` — the stamp {@link as400PublishFileName} joins on. */
const PUBLISH_TIMESTAMP_SEGMENT_RE = /^\d{12}$/;

/**
 * Read a published filename back into what it says it is, or say why it can't be
 * read.
 *
 * Case-insensitive, and tolerant of a leading path or an added extension, since
 * a name reaches this from a file picker as often as from the folder we wrote it
 * to — a `.txt` an operating system appended changes nothing about which file
 * this is. Everything else is strict: a stale `GNT` source segment or a branch
 * on a worksheet that should not carry one shifts every later segment, so it is
 * refused rather than read as a worksheet or a stamp.
 */
export function parseAs400PublishFileName(
  fileName: string,
):
  | { ok: true; parsed: ParsedAs400PublishFileName }
  | { ok: false; problem: As400PublishFileNameProblem } {
  const base = (fileName.split(/[\\/]/).pop() ?? "")
    .trim()
    .replace(/\.[A-Za-z0-9]+$/, "")
    .toUpperCase();
  const segments = base.split("_");
  if (segments.length < 3 || segments.length > 4) {
    return { ok: false, problem: "SHAPE" };
  }

  const mode = as400PublishModeFromSegment(segments[0] ?? "");
  if (mode === null) return { ok: false, problem: "MODE" };
  const group = as400PublishWorksheetFromSegment(segments[1] ?? "");
  if (group === null) return { ok: false, problem: "WORKSHEET" };

  // A four-segment name is a branch-named file and a three-segment one is not,
  // so the arity has to agree with the worksheet: `I2_PRICE_C_2026…` would
  // otherwise read `C` as the stamp, and `I2_ITEM_2026…` would read the stamp
  // as the branch.
  const namesBranch = as400PublishNamesBranch(group);
  if (namesBranch !== (segments.length === 4)) {
    return { ok: false, problem: "BRANCH" };
  }
  const branch = namesBranch ? (segments[2] ?? "") : null;
  if (branch !== null && !PUBLISH_BRANCH_SEGMENT_RE.test(branch)) {
    return { ok: false, problem: "BRANCH" };
  }

  const timestamp = segments[segments.length - 1] ?? "";
  if (!PUBLISH_TIMESTAMP_SEGMENT_RE.test(timestamp)) {
    return { ok: false, problem: "TIMESTAMP" };
  }
  return { ok: true, parsed: { mode, group, branch, timestamp } };
}

/**
 * The location-list file of an all-locations (`@`) batch: one location code per
 * line, CRLF after each, named `I1_ITMSL_<stamp>` / `I2_ITMSL_<stamp>` — no
 * branch segment, like every file but the item file.
 */
export const AS400_PUBLISH_LOCATION_LIST_SEGMENT = "ITMSL";

/**
 * The stock location-list file of an all-locations (`@`) batch: `I1_STKSL_<stamp>`
 * / `I2_STKSL_<stamp>`, one **stocking** location code per line (`D`, `E`, `M`
 * for RD) — the code space the stock worksheets are keyed by, not the item
 * branches `ITMSL` lists. The batch's STOCK and STKAD files then carry each row
 * once with the location left blank, and AS/400 applies them at every location
 * this file lists.
 */
export const AS400_PUBLISH_STOCK_LOCATION_LIST_SEGMENT = "STKSL";

/**
 * How a division's locations are published:
 *
 * - `perLocation` — one batch per location (branch), each named for it.
 * - `allLocations` — one batch for every location: the item file is named `@`,
 *   and an `ITMSL` file lists the locations. Rexel's loader applies the whole
 *   drop once per location listed, so each file carries **one** copy of each
 *   row. That is only true to the review when every location's files are
 *   identical; when one differs the batch is refused, naming the worksheet,
 *   the locations and the product that differ — never silently picking one.
 */
export const As400PublishLocationModeSchema = z.enum([
  "perLocation",
  "allLocations",
]);
export type As400PublishLocationMode = z.infer<
  typeof As400PublishLocationModeSchema
>;

/** How many lines of the file the preview carries. */
export const AS400_PUBLISH_PREVIEW_LINES = 10;

/**
 * One worksheet's file within a batch: what it would be called, what it carries,
 * and enough of it to read before writing it.
 */
export const As400PublishFileSchema = z.object({
  /**
   * The worksheet this file is for. Null for an all-locations batch's location
   * list ({@link AS400_PUBLISH_LOCATION_LIST_SEGMENT}), which is no worksheet.
   */
  group: As400ExportGroupSchema.nullable(),
  /** The full name it would be written under. */
  fileName: z.string(),
  /**
   * Data rows it holds.
   *
   * Zero only for the item file, which is in every batch whether or not the
   * review changed any item data: it is the only file that names the branch, so
   * it is what tells AS/400 where the rest of the batch loads. Every other file
   * with no rows is left out.
   */
  rowCount: z.number().int().nonnegative(),
  byteSize: z.number().int().nonnegative(),
  /**
   * The file's first {@link AS400_PUBLISH_PREVIEW_LINES} lines, verbatim — tabs
   * and all, so what the dialog shows is what gets written.
   */
  bodyPreview: z.string(),
  /** True when the file has more lines than the preview shows. */
  bodyPreviewTruncated: z.boolean(),
});

/**
 * One batch the review could publish: every file for a single branch of a single
 * division, in one mode.
 *
 * A batch is what goes into the folder together, because AS/400 loads a branch at
 * a time and reads every file it finds. Adds and changes stay in separate
 * batches: a record has to exist before a change to it applies, and once both are
 * in the folder we no longer control which their loader reads first.
 *
 * **The item file is always in the batch**, even with no rows. Every file is
 * branch-scoped, but only the item file's *name* says which branch — so it is
 * what tells AS/400 where the whole run loads. A price change sent without it
 * has nothing to say where the prices belong.
 */
export const As400PublishBatchSchema = z.object({
  mode: As400PublishModeSchema,
  /** The division (DataSource) whose records this batch carries. */
  dataSourceId: z.string().uuid(),
  /** That division's title, for labelling the batch in the dialog. */
  divisionTitle: z.string(),
  /**
   * The branch every file in the batch loads into — the third filename segment.
   * Null for records that carry no branch key of their own, which cannot be
   * named and so cannot be published; see
   * {@link As400PublishBatchSchema.shape.unavailableReason}.
   */
  branch: z.string().nullable(),
  /**
   * The locations the batch loads at: its branch, or — for an all-locations
   * (`@`) batch — every location its `ITMSL` file lists.
   */
  locations: z.array(z.string()),
  /**
   * The stocking locations an all-locations batch's `STKSL` file lists — where
   * its STOCK and STKAD rows load. Empty for a per-location batch, whose stock
   * rows carry their location in the row.
   */
  stockLocations: z.array(z.string()),
  /** The files, one per worksheet with something to send for this branch. */
  files: z.array(As400PublishFileSchema),
  /** Rows across every file in the batch. */
  totalRows: z.number().int().nonnegative(),
  /** Bytes across every file in the batch. */
  totalBytes: z.number().int().nonnegative(),
  /**
   * Why this batch can't be published, when it can't — so work the review
   * produces is never quietly missing from the list. Null when it can.
   */
  unavailableReason: z.string().nullable(),
});

/** A file already sitting in the publish folder. */
export const As400PublishFolderEntrySchema = z.object({
  /** Blob name within the folder. */
  name: z.string(),
  /**
   * True once AS/400 has picked the file up — it marks a file it has read with
   * a `@@` prefix, and usually moves it into `processed/`.
   *
   * An acknowledged file is finished business: it is listed so the folder can be
   * seen as it is, but it does not block the next publish.
   */
  acknowledged: z.boolean(),
});

/**
 * No `group` and no filters: a publish covers every worksheet the review has
 * edits in, so the preview is asked of the review rather than of whichever view
 * happens to be on screen.
 *
 * **Deliberately no `advancedFilters`.** The catalog's filters are written
 * against the columns of one worksheet, and a row is matched by treating a
 * column the worksheet doesn't have as null — so a filter on an item-only column
 * like `IEDESC` matches nothing in the price or purchase worksheets and would
 * empty their files. A view filter narrows what someone is looking at; it has no
 * business deciding which of a branch's records reach AS/400.
 */
export const As400PublishPreviewInputSchema = z.object({
  dataSourceIds: z.array(z.string().uuid()).min(1).max(50),
  changeSetId: z.string().uuid(),
  /**
   * Force every batch to `I1` or `I2` ({@link As400UploadModeOverrideSchema}):
   * the review's adds and changes then go up together, in that one mode.
   */
  modeOverride: As400UploadModeOverrideSchema,
  /** One batch per location (the default), or one `@` batch for them all. */
  locationMode: As400PublishLocationModeSchema.default("perLocation"),
});

export const As400PublishPreviewResponseSchema = z.object({
  /** Blob container the folder lives in. */
  container: z.string(),
  /** Folder within it that AS/400 polls. */
  folder: z.string(),
  /**
   * Worksheets this review has edits in that cannot be published, because Rexel
   * hasn't given us the filename segment their loader routes them by.
   *
   * Reported rather than skipped: a batch that silently left a worksheet out
   * would load a partial picture of the same records, and nothing downstream
   * would say so. Those worksheets have to be downloaded and uploaded by hand
   * until the segment is known.
   */
  unnameableWorksheets: z.array(As400ExportGroupSchema),
  /**
   * Worksheets this review has edits in that reached **no batch at all** —
   * their changed records came back with no branch to file them under, so there
   * was nowhere to put them.
   *
   * The same principle as {@link unnameableWorksheets} and the more likely of
   * the two in practice: an edit that produces no branch would otherwise just be
   * absent from the dialog, which reads as "I have nothing to publish" rather
   * than "I could not place this". Usually means the ingest rows behind the edit
   * carry a blank location.
   */
  worksheetsWithoutBranch: z.array(As400ExportGroupSchema),
  /**
   * Whether the folder is clear to write into: true when nothing in it is still
   * waiting to be picked up.
   *
   * Rexel's job cannot cope with a file changing under it, so at most one file of
   * ours may be waiting in the folder at a time. What counts as waiting is a file
   * AS/400 has *not* acknowledged — an acknowledged (`@@`) file has been read and
   * is out of the way, so it does not hold the next publish back.
   */
  folderClear: z.boolean(),
  /**
   * What is in the folder now — the blocking files and the acknowledged ones
   * alike, so the folder can be seen as it is rather than only when it blocks.
   */
  folderEntries: z.array(As400PublishFolderEntrySchema),
  /**
   * Set when the folder could not be read at all (storage not configured, or
   * unreachable). Publishing is refused while it is set: an unread folder cannot
   * be shown to be empty.
   */
  folderError: z.string().nullable(),
  /** The batches this review could publish, adds first. */
  batches: z.array(As400PublishBatchSchema),
});

/**
 * Which batch to publish — one branch of one division, in one mode. Named by
 * what it carries rather than by an index into the preview, so a stale dialog
 * cannot publish a different batch than the one that was read.
 *
 * Which worksheets it covers, and which rows each carries, is decided
 * server-side at publish time from the review as it stands then. The dialog does
 * not get to send a subset — a branch goes up whole — and, for the same reason,
 * it does not get to send filters; see
 * {@link As400PublishPreviewInputSchema}.
 */
export const As400PublishInputSchema = z.object({
  changeSetId: z.string().uuid(),
  dataSourceId: z.string().uuid(),
  mode: As400PublishModeSchema,
  /**
   * One branch, or `@` for the division's all-locations batch in this mode
   * ({@link As400PublishLocationModeSchema}): the server rebuilds it from every
   * location's batch, and refuses it if they no longer match.
   */
  branch: z.string().min(1).max(4),
  /** The override the batch was previewed under — see the preview input. */
  modeOverride: As400UploadModeOverrideSchema,
});

/** One file as it was actually written. */
export const As400PublishWrittenFileSchema = z.object({
  /** Null for the location list. */
  group: As400ExportGroupSchema.nullable(),
  fileName: z.string(),
  /** Full blob key, folder included. */
  blobKey: z.string(),
  rowCount: z.number().int().nonnegative(),
  byteSize: z.number().int().nonnegative(),
});

export const As400PublishResponseSchema = z.object({
  /** Every file written, in the order they went up. */
  files: z.array(As400PublishWrittenFileSchema),
  container: z.string(),
  totalRows: z.number().int().nonnegative(),
  totalBytes: z.number().int().nonnegative(),
  publishedAt: z.string(),
});

export const As400PublishAccessResponseSchema = z.object({
  /**
   * Whether the caller may publish. Gently-internal only for now: the file
   * lands in Rexel's live AS/400 queue, and the process around it (when to
   * publish, who waits for the acknowledgement) is still ours to run.
   */
  canPublish: z.boolean(),
  container: z.string(),
  folder: z.string(),
});

export const as400PublishContracts = {
  access: oc
    .input(z.object({}))
    .output(As400PublishAccessResponseSchema)
    .route({
      method: "GET",
      summary: "Check permission to publish catalog files to the ERP",
      description:
        "Returns whether the current user may publish catalog upload files directly to the ERP's pickup folder, and which folder they would go to.",
    }),
  preview: oc
    .input(As400PublishPreviewInputSchema)
    .output(As400PublishPreviewResponseSchema)
    .route({
      method: "GET",
      summary: "Preview ERP catalog upload batches",
      description:
        "For a change set and divisions, lists the per-branch batches of upload files that could be published to the ERP, with file names, row counts, first lines, blockers and whether the pickup folder is clear.",
    }),
  download: oc
    .input(As400PublishInputSchema)
    .output(ExportFileStreamSchema)
    .route({
      method: "GET",
      summary: "Download an ERP catalog upload batch",
      description:
        "Downloads one branch's batch as a zip of the exact upload files a publish would write, without sending anything to the ERP.",
    }),
  publish: oc
    .input(As400PublishInputSchema)
    .output(As400PublishResponseSchema)
    .route({
      method: "POST",
      summary: "Publish catalog changes to the ERP",
      description:
        "Writes one branch's full set of change-set upload files into the ERP's pickup folder for loading. Refused while an earlier file is still waiting there.",
    }),
} as const;

export type As400PublishFile = z.infer<typeof As400PublishFileSchema>;
export type As400PublishBatch = z.infer<typeof As400PublishBatchSchema>;
export type As400PublishWrittenFile = z.infer<
  typeof As400PublishWrittenFileSchema
>;
export type As400PublishFolderEntry = z.infer<
  typeof As400PublishFolderEntrySchema
>;
export type As400PublishPreviewInput = z.infer<
  typeof As400PublishPreviewInputSchema
>;
export type As400PublishPreviewResponse = z.infer<
  typeof As400PublishPreviewResponseSchema
>;
export type As400PublishInput = z.infer<typeof As400PublishInputSchema>;
export type As400PublishResponse = z.infer<typeof As400PublishResponseSchema>;
export type As400PublishAccessResponse = z.infer<
  typeof As400PublishAccessResponseSchema
>;
